import React from 'react';
import { NavLink } from 'react-router';
import { useLayoutStore } from '@store';
import { useI18n } from '@shared/i18n';
import { cx } from '@shared/utils';
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GridViewIcon from '@mui/icons-material/GridView';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './Sidebar.module.css';

interface NavItem {
    to: string;
    label: string;
    icon: React.ReactNode;
}

const Sidebar: React.FC = (): React.ReactElement => {
    const { t } = useI18n();
    const isNavbarExpanded = useLayoutStore((s): boolean => s.isNavbarExpanded);
    const toggleNavbar = useLayoutStore((s): (() => void) => s.toggleNavbar);

    const navItems: NavItem[] = [
        { to: '/', label: t('navigation.overview'), icon: <HomeIcon /> },
        ...(import.meta.env.VITE_DEV_MODE === 'true'
            ? [
                  {
                      to: '/calendar',
                      label: t('navigation.calendar'),
                      icon: <CalendarMonthIcon />,
                  },
              ]
            : []),
        {
            to: '/requests',
            label: t('navigation.requests'),
            icon: <AssignmentIcon />,
        },
        ...(import.meta.env.VITE_DEV_MODE === 'true'
            ? [
                  {
                      to: '/patterns',
                      label: t('navigation.patterns'),
                      icon: <GridViewIcon />,
                  },
              ]
            : []),
        {
            to: '/departments',
            label: t('navigation.departments'),
            icon: <BusinessIcon />,
        },
        {
            to: '/profile',
            label: t('navigation.profile'),
            icon: <PersonOutlineIcon />,
        },
    ];

    return (
        <nav
            className={cx(styles.nav, isNavbarExpanded && styles.navExpanded)}
            aria-label={t('layout.mainNavigation')}
        >
            <div className={styles.navSection}>
                <>
                    <div className={styles.logo}>
                        <img
                            src="/images/logo-orange.svg"
                            alt="Dynamon"
                            className={styles.logoImage}
                        />
                    </div>
                    <hr className={styles.divider} />
                </>
                <div className={styles.navItems}>
                    {navItems.map((item): React.ReactElement => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end
                            className={({ isActive }): string =>
                                cx(
                                    styles.navLink,
                                    isActive && styles.navLinkActive,
                                )
                            }
                        >
                            <span className={styles.icon}>{item.icon}</span>
                            <span className={styles.label}>{item.label}</span>
                        </NavLink>
                    ))}
                </div>
            </div>
            <div>
                <hr className={styles.divider} />
                <button
                    className={styles.toggleButton}
                    onClick={toggleNavbar}
                    aria-label={
                        isNavbarExpanded
                            ? t('layout.toggleSidebar.hide')
                            : t('layout.toggleSidebar.show')
                    }
                >
                    <span className={styles.toggleIcon}>
                        {isNavbarExpanded ? (
                            <ChevronLeftIcon />
                        ) : (
                            <ChevronRightIcon />
                        )}
                    </span>
                    <span className={styles.toggleLabel}>
                        {t('layout.toggleSidebar.label')}
                    </span>
                </button>
            </div>
        </nav>
    );
};

export default Sidebar;
